import React, { useEffect, useState, useCallback, useRef } from 'react';
import Component from '../../components/Component';
import { BASE_URL } from '../../components/AppConfig';
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, Chip, CircularProgress,
  Paper, Tab, Tabs, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';

function getHeaders() {
  const t = sessionStorage.getItem('sessionToken');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

function TokenChip({ input, output }) {
  if (!input && !output) return <Typography variant="caption" color="text.secondary">—</Typography>;
  return <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{input ?? '?'} → {output ?? '?'}</Typography>;
}

function Tip({ text }) {
  return <Tooltip title={text} arrow placement="top"><InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', ml: 0.5, verticalAlign: 'middle', cursor: 'help' }} /></Tooltip>;
}

function ActionBtn({ label, tip, onClick, loading, disabled, color = 'primary', variant = 'outlined', size = 'small' }) {
  return (
    <Tooltip title={tip} arrow placement="bottom">
      <span>
        <Button variant={variant} size={size} color={color} onClick={onClick} disabled={disabled || loading}>
          {loading ? <CircularProgress size={18} /> : label}
        </Button>
      </span>
    </Tooltip>
  );
}

// ─── Matches dropdown (loads from /admin/match-hashtag-topics/matches) ────────
function MatchesDropdown({ value, onChange }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const timer = useRef(null);

  const fetchMatches = useCallback(async (search = '') => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: '100' });
      if (search) p.append('search', search);
      const res = await fetch(`${BASE_URL}/admin/match-hashtag-topics/matches?${p}`, { credentials: 'include', headers: getHeaders() });
      const data = await res.json().catch(() => ({}));
      setOptions(data.data?.matches ?? []);
    } catch { setOptions([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMatches(); }, [fetchMatches]);

  const handleInput = useCallback((_, v) => {
    setInputVal(v);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => fetchMatches(v), 400);
  }, [fetchMatches]);

  return (
    <Autocomplete
      options={options}
      loading={loading}
      value={value}
      inputValue={inputVal}
      onInputChange={handleInput}
      onChange={(_, m) => onChange(m)}
      getOptionLabel={m => m ? `${m.match_id} — ${m.teama__short_name || '?'} vs ${m.teamb__short_name || '?'}` : ''}
      isOptionEqualToValue={(o, v) => o?.match_id === v?.match_id}
      renderInput={p => <TextField {...p} label="Select Match" size="small" sx={{ minWidth: 320 }}
        InputProps={{ ...p.InputProps, endAdornment: <>{loading ? <CircularProgress size={18} /> : null}{p.InputProps.endAdornment}</> }} />}
      renderOption={(props, m) => (
        <li {...props} key={m.match_id}>
          <Box>
            <Typography variant="body2" fontWeight={600}>{m.teama__short_name || '?'} vs {m.teamb__short_name || '?'}</Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {m.match_id} · {m.title || ''} · {m.status_str || ''} · {m.date_start_ist ? new Date(m.date_start_ist).toLocaleDateString() : ''}
            </Typography>
          </Box>
        </li>
      )}
    />
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Tab 1: Feed Summary History
// ═══════════════════════════════════════════════════════════════════════════════
function HistoryTab() {
  const [rows, setRows] = useState([]);
  const [limit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterMatchId, setFilterMatchId] = useState('');
  const [triggerMatchId, setTriggerMatchId] = useState('');
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [success, setSuccess] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const p = new URLSearchParams({ limit, offset });
      if (filterMatchId.trim()) p.append('matchId', filterMatchId.trim());
      const res = await fetch(`${BASE_URL}/admin/feed-summary/list?${p}`, { credentials: 'include', headers: getHeaders() });
      const data = await res.json().catch(() => ({}));
      const list = data.data?.summaries ?? [];
      setRows(Array.isArray(list) ? list : []);
      setTotal(data.data?.total ?? list.length);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [limit, offset, filterMatchId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleTrigger = async () => {
    const mid = triggerMatchId.trim();
    if (!mid) { setError('Enter a Match ID'); return; }
    setTriggerLoading(true); setError(''); setSuccess('');
    try {
      const res = await fetch(`${BASE_URL}/admin/feed-summary/trigger/${mid}`, { method: 'POST', credentials: 'include', headers: getHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.message || 'Failed'); return; }
      setSuccess(`Triggered for match ${mid} — generated: ${data.data?.generated ?? 0}`);
      setTriggerMatchId('');
      fetchData();
    } catch (e) { setError(e.message); } finally { setTriggerLoading(false); }
  };

  const page = Math.floor(offset / limit) + 1;
  const pages = total > 0 ? Math.ceil(total / limit) : 1;

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Log of all AI-generated feed summaries (from <code>ai_match_updates</code> table).
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" fontWeight={600}>Trigger</Typography>
        <TextField label="Match ID" size="small" type="number" value={triggerMatchId}
          onChange={e => setTriggerMatchId(e.target.value)} placeholder="e.g. 94723" sx={{ width: 140 }} />
        <Button variant="contained" size="small" onClick={handleTrigger} disabled={triggerLoading || !triggerMatchId.trim()}>
          {triggerLoading ? <CircularProgress size={20} color="inherit" /> : 'Trigger'}
        </Button>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField label="Filter by Match ID" size="small" type="number" value={filterMatchId}
          onChange={e => { setFilterMatchId(e.target.value); setOffset(0); }} sx={{ width: 160 }} />
        <Button variant="outlined" size="small" onClick={fetchData} disabled={loading}>Refresh</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box> : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>ID</TableCell><TableCell>Match</TableCell><TableCell>Summary</TableCell>
              <TableCell>Model</TableCell><TableCell>Tokens (in→out)</TableCell><TableCell>Created</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center"><Typography variant="body2" color="text.secondary">No feed summaries found.</Typography></TableCell></TableRow>
              ) : rows.map((s, i) => (
                <TableRow key={s.id ?? i}>
                  <TableCell>{s.id}</TableCell>
                  <TableCell>{s.match_id}</TableCell>
                  <TableCell><Typography variant="body2" sx={{ maxWidth: 350, wordBreak: 'break-word' }}>{s.line ?? '—'}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{s.llm_model ?? '—'}</Typography></TableCell>
                  <TableCell><TokenChip input={s.llm_input_tokens} output={s.llm_output_tokens} /></TableCell>
                  <TableCell><Typography variant="caption">{s.created_at ? new Date(s.created_at).toLocaleString() : '—'}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Typography variant="caption" color="text.secondary">Page {page}/{pages}{total > 0 && ` · ${total} total`}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" disabled={offset === 0} onClick={() => setOffset(o => Math.max(o - limit, 0))}>Prev</Button>
          <Button size="small" variant="outlined" disabled={offset + limit >= total} onClick={() => setOffset(o => o + limit)}>Next</Button>
        </Box>
      </Box>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Tab 2: Match Hashtag Topics
// ═══════════════════════════════════════════════════════════════════════════════
function MatchTopicsTab() {
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [topics, setTopics] = useState([]);
  const [allTopicsCount, setAllTopicsCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [triggerLoading, setTriggerLoading] = useState('');
  const [rowTriggerLoading, setRowTriggerLoading] = useState(null);

  const matchId = selectedMatch?.match_id;

  // Fetch total topic count on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/admin/match-hashtag-topics/match/0?limit=1`, { credentials: 'include', headers: getHeaders() });
        const data = await res.json().catch(() => ({}));
        // Use total from any match query just to show we have data; or use a dedicated count
      } catch {}
    })();
  }, []);

  const fetchTopics = useCallback(async (mid) => {
    if (!mid) return;
    setLoading(true); setError(''); setSuccess(''); setTopics([]);
    try {
      const res = await fetch(`${BASE_URL}/admin/match-hashtag-topics/match/${mid}?limit=100`, { credentials: 'include', headers: getHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.message || `Failed (HTTP ${res.status})`); return; }
      const list = data.data?.topics ?? [];
      setTopics(list);
      setAllTopicsCount(data.data?.total ?? list.length);
      if (list.length === 0) setError('No topics found. Click "Refresh Scores" to create topic rows from feeds.');
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (matchId) fetchTopics(matchId); }, [matchId, fetchTopics]);

  const triggerAction = async (action) => {
    if (!matchId) { setError('Select a match first'); return; }
    setTriggerLoading(action); setError(''); setSuccess('');
    try {
      const url = action === 'content'
        ? `${BASE_URL}/admin/match-hashtag-topics/trigger/content/${matchId}`
        : action === 'feed-summary'
        ? `${BASE_URL}/admin/match-hashtag-topics/trigger/feed-summary/${matchId}`
        : `${BASE_URL}/admin/match-hashtag-topics/refresh-scores/${matchId}`;
      const res = await fetch(url, { method: 'POST', credentials: 'include', headers: getHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.message || 'Failed'); return; }
      const gen = data.data?.generated ?? data.data?.rowsUpserted ?? 0;
      const total = data.data?.total ?? '';
      setSuccess(`${action}: ${gen}${total ? ` / ${total}` : ''} ${action === 'scores' ? 'rows upserted' : 'generated'}`);
      fetchTopics(matchId);
    } catch (e) { setError(e.message); } finally { setTriggerLoading(''); }
  };

  const triggerRowFeedSummary = async (rowId) => {
    setRowTriggerLoading(rowId); setError(''); setSuccess('');
    try {
      const res = await fetch(`${BASE_URL}/admin/match-hashtag-topics/trigger/feed-summary/row/${rowId}`, { method: 'POST', credentials: 'include', headers: getHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.message || 'Failed'); return; }
      setSuccess(`Feed summary regenerated for row ${rowId}: ${(data.data?.summarizer ?? '').slice(0, 50)}...`);
      fetchTopics(matchId);
    } catch (e) { setError(e.message); } finally { setRowTriggerLoading(null); }
  };

  const [titleRegenLoading, setTitleRegenLoading] = useState(null);
  const triggerRowTitleRegen = async (rowId) => {
    setTitleRegenLoading(rowId); setError(''); setSuccess('');
    try {
      const res = await fetch(`${BASE_URL}/admin/match-hashtag-topics/trigger/title/row/${rowId}`, { method: 'POST', credentials: 'include', headers: getHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.message || 'Failed'); return; }
      setSuccess(`Title regenerated for row ${rowId}: "${data.data?.title ?? ''}"`);
      fetchTopics(matchId);
    } catch (e) { setError(e.message); } finally { setTitleRegenLoading(null); }
  };

  const [imageUploadLoading, setImageUploadLoading] = useState(null);
  const uploadRowImage = async (rowId, file) => {
    if (!file) return;
    setImageUploadLoading(rowId); setError(''); setSuccess('');
    try {
      const fd = new FormData();
      fd.append('image', file);
      const t = sessionStorage.getItem('sessionToken');
      const res = await fetch(`${BASE_URL}/admin/match-hashtag-topics/row/${rowId}/image`, {
        method: 'PUT',
        credentials: 'include',
        headers: t ? { Authorization: `Bearer ${t}` } : {},
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.message || 'Image upload failed'); return; }
      setSuccess(`Image uploaded for row ${rowId}`);
      fetchTopics(matchId);
    } catch (e) { setError(e.message); } finally { setImageUploadLoading(null); }
  };

  const clearRowImage = async (rowId) => {
    setImageUploadLoading(rowId); setError(''); setSuccess('');
    try {
      const res = await fetch(`${BASE_URL}/admin/match-hashtag-topics/row/${rowId}/image`, { method: 'DELETE', credentials: 'include', headers: getHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.message || 'Failed to clear image'); return; }
      setSuccess(`Image cleared for row ${rowId}`);
      fetchTopics(matchId);
    } catch (e) { setError(e.message); } finally { setImageUploadLoading(null); }
  };

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Hashtag topics are auto-created from feed hashtags. Select a match to view its topics, generate content, or regenerate feed summaries.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <MatchesDropdown value={selectedMatch} onChange={setSelectedMatch} />
        {matchId && <Chip label={`Match ${matchId} · ${allTopicsCount} topics`} color="primary" variant="outlined" />}
      </Box>

      {matchId && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ mr: 1 }}>Actions:</Typography>
          <ActionBtn label="1. Refresh Scores" tip="Recalculate scores from feeds. Creates topic rows if they don't exist yet." onClick={() => triggerAction('scores')} loading={triggerLoading === 'scores'} disabled={!!triggerLoading} color="secondary" />
          <ActionBtn label="2. Generate Content" tip="Generate title + summarizer + poll options for topics without a title and score >= 30." onClick={() => triggerAction('content')} loading={triggerLoading === 'content'} disabled={!!triggerLoading} />
          <ActionBtn label="3. Generate All Feed Summaries" tip="Generate feed summaries for all qualifying topics (score >= 30, score grew by 30+ since last run)." onClick={() => triggerAction('feed-summary')} loading={triggerLoading === 'feed-summary'} disabled={!!triggerLoading} />
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box> : topics.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Topic <Tip text="Hashtag topic key" /></TableCell>
              <TableCell>Scope <Tip text="match_only / match_team / match_player" /></TableCell>
              <TableCell>Score <Tip text="post_count + 5 * unique_users. >= 30 for GenAI." /></TableCell>
              <TableCell>Posts</TableCell>
              <TableCell>Title <Tip text="Generated title (13-15 chars)" /></TableCell>
              <TableCell>Feed Summary <Tip text="Fan-voiced summary from feed summariser" /></TableCell>
              <TableCell>Options</TableCell>
              <TableCell>SPC <Tip text="Score when last summarised. Regenerates at SPC + 30." /></TableCell>
              <TableCell>Image <Tip text="Admin-uploaded talk image. Click thumbnail or upload area to replace." /></TableCell>
              <TableCell>Model</TableCell>
              <TableCell>Tokens</TableCell>
              <TableCell>Action</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {topics.map((t, i) => (
                <TableRow key={t.id ?? i} sx={{ bgcolor: t.score < 30 ? 'action.disabledBackground' : t.summarizer ? 'inherit' : 'action.hover' }}>
                  <TableCell>{t.id}</TableCell>
                  <TableCell><Typography variant="body2" fontWeight={600}>{t.topic_key}</Typography></TableCell>
                  <TableCell><Chip label={t.scope} size="small" variant="outlined" /></TableCell>
                  <TableCell><Typography variant="body2" color={t.score >= 30 ? 'success.main' : 'text.secondary'} fontWeight={t.score >= 30 ? 600 : 400}>{t.score}</Typography></TableCell>
                  <TableCell>{t.post_count}</TableCell>
                  <TableCell><Typography variant="caption" sx={{ maxWidth: 100, display: 'block' }}>{t.title ?? <em style={{ color: '#bbb' }}>—</em>}</Typography></TableCell>
                  <TableCell><Typography variant="body2" sx={{ maxWidth: 200, wordBreak: 'break-word', fontSize: '0.75rem' }}>{t.summarizer ?? <em style={{ color: '#bbb' }}>—</em>}</Typography></TableCell>
                  <TableCell>{t.options?.length > 0 ? t.options.map((o, j) => <Chip key={j} label={o} size="small" sx={{ mr: 0.5, mb: 0.5 }} />) : '—'}</TableCell>
                  <TableCell>{t.summary_post_count ?? '—'}</TableCell>
                  <TableCell sx={{ minWidth: 110 }}>
                    {imageUploadLoading === t.id ? (
                      <CircularProgress size={18} />
                    ) : t.image_url ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                        <Box
                          component="img"
                          src={t.image_url}
                          alt={t.title || `talk ${t.id}`}
                          sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd' }}
                        />
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Button component="label" size="small" variant="text" sx={{ minWidth: 0, p: 0.25, fontSize: 10 }}>
                            Replace
                            <input type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; uploadRowImage(t.id, f); }} />
                          </Button>
                          <Button size="small" variant="text" color="error" sx={{ minWidth: 0, p: 0.25, fontSize: 10 }} onClick={() => clearRowImage(t.id)}>
                            Clear
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Button component="label" size="small" variant="outlined" sx={{ fontSize: 11, py: 0.25 }}>
                        Upload
                        <input type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; uploadRowImage(t.id, f); }} />
                      </Button>
                    )}
                  </TableCell>
                  <TableCell><Typography variant="caption">{t.llm_model ?? '—'}</Typography></TableCell>
                  <TableCell><TokenChip input={t.llm_input_tokens} output={t.llm_output_tokens} /></TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                      <Tooltip title="Regenerate feed summary for this topic" arrow>
                        <span>
                          <Button size="small" variant="outlined" onClick={() => triggerRowFeedSummary(t.id)}
                            disabled={rowTriggerLoading === t.id || titleRegenLoading === t.id || !!triggerLoading}
                            startIcon={rowTriggerLoading === t.id ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 16 }} />}>
                            {rowTriggerLoading === t.id ? '' : 'Regen'}
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title="Regenerate ONLY the title — leaves summary and options untouched" arrow>
                        <span>
                          <Button size="small" variant="text" color="secondary" onClick={() => triggerRowTitleRegen(t.id)}
                            disabled={titleRegenLoading === t.id || rowTriggerLoading === t.id || !!triggerLoading}
                            startIcon={titleRegenLoading === t.id ? <CircularProgress size={12} /> : <RefreshIcon sx={{ fontSize: 14 }} />}
                            sx={{ fontSize: 11 }}>
                            {titleRegenLoading === t.id ? '' : 'Regen Title'}
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!matchId && !loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <Typography variant="body1" color="text.secondary">Select a match from the dropdown above to view its hashtag topics.</Typography>
        </Box>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Tab 3: Feed Topic Summaries (feedtopics table)
// ═══════════════════════════════════════════════════════════════════════════════
function FeedTopicsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [triggerAllLoading, setTriggerAllLoading] = useState(false);
  const [triggerRowId, setTriggerRowId] = useState(null);
  const [filterCid, setFilterCid] = useState('');
  const [filterMid, setFilterMid] = useState('');

  const fetchList = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const p = new URLSearchParams({ limit: '50' });
      if (filterCid.trim()) p.append('cid', filterCid.trim());
      if (filterMid.trim()) p.append('mid', filterMid.trim());
      const res = await fetch(`${BASE_URL}/admin/feed-topic-summary/list?${p}`, { credentials: 'include', headers: getHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.message || `Failed (HTTP ${res.status})`); return; }
      setRows(data.data?.topics ?? []);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [filterCid, filterMid]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const triggerAll = async (force) => {
    setTriggerAllLoading(true); setError(''); setSuccess('');
    try {
      const res = await fetch(`${BASE_URL}/admin/feed-topic-summary/trigger-all${force ? '?force=true' : ''}`, { method: 'POST', credentials: 'include', headers: getHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.message || 'Failed'); return; }
      setSuccess(`Generated ${data.data?.generated ?? 0} of ${data.data?.total ?? 0}`);
      fetchList();
    } catch (e) { setError(e.message); } finally { setTriggerAllLoading(false); }
  };

  const triggerOne = async (id) => {
    setTriggerRowId(id); setError(''); setSuccess('');
    try {
      const res = await fetch(`${BASE_URL}/admin/feed-topic-summary/trigger/${id}`, { method: 'POST', credentials: 'include', headers: getHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.message || 'Failed'); return; }
      setSuccess(`Generated for topic ${id}`);
      fetchList();
    } catch (e) { setError(e.message); } finally { setTriggerRowId(null); }
  };

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Standalone discussion topics (feedtopics table). Summaries regenerate every +10 new posts.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField label="Filter by CID" size="small" type="number" value={filterCid}
          onChange={e => setFilterCid(e.target.value)} placeholder="Competition ID" sx={{ width: 150 }} />
        <TextField label="Filter by MID" size="small" type="number" value={filterMid}
          onChange={e => setFilterMid(e.target.value)} placeholder="Match ID" sx={{ width: 150 }} />
        <ActionBtn label="Search" tip="Filter by competition ID and/or match ID" onClick={fetchList} loading={loading} variant="contained" />
        {(filterCid || filterMid) && (
          <Button size="small" variant="text" onClick={() => { setFilterCid(''); setFilterMid(''); }}>Clear</Button>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <ActionBtn label="Refresh" tip="Reload list" onClick={fetchList} loading={loading} variant="outlined" />
        <ActionBtn label="Generate Qualifying" tip="Generate for topics with 10+ posts needing summary" onClick={() => triggerAll(false)} loading={triggerAllLoading} variant="contained" />
        <ActionBtn label="Force Regenerate All" tip="Regenerate ALL topics with 10+ posts" onClick={() => triggerAll(true)} loading={triggerAllLoading} color="warning" variant="contained" />
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box> : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>ID</TableCell><TableCell>Topic</TableCell><TableCell>CID</TableCell><TableCell>MID</TableCell><TableCell>Feeds</TableCell>
              <TableCell>Summarizer</TableCell><TableCell>Options</TableCell><TableCell>SPC</TableCell>
              <TableCell>Model</TableCell><TableCell>Tokens</TableCell><TableCell>Action</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={11} align="center"><Typography variant="body2" color="text.secondary">No feed topics found.</Typography></TableCell></TableRow>
              ) : rows.map((r, i) => (
                <TableRow key={r.id ?? i} sx={{ bgcolor: r.disabled ? 'action.disabledBackground' : 'inherit' }}>
                  <TableCell>{r.id}</TableCell>
                  <TableCell><Typography variant="body2" fontWeight={600}>{r.topic_name}{r.disabled && <Chip label="disabled" size="small" sx={{ ml: 1 }} />}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{r.cid ?? '—'}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{r.mid ?? '—'}</Typography></TableCell>
                  <TableCell>{r.feed_count}</TableCell>
                  <TableCell><Typography variant="body2" sx={{ maxWidth: 250, wordBreak: 'break-word', fontSize: '0.75rem' }}>{r.summarizer ?? <em style={{ color: '#bbb' }}>—</em>}</Typography></TableCell>
                  <TableCell>{r.options?.length > 0 ? r.options.map((o, j) => <Chip key={j} label={typeof o === 'string' ? o : o.label || JSON.stringify(o)} size="small" sx={{ mr: 0.5, mb: 0.5 }} />) : '—'}</TableCell>
                  <TableCell>{r.summary_post_count ?? '—'}</TableCell>
                  <TableCell><Typography variant="caption">{r.llm_model ?? '—'}</Typography></TableCell>
                  <TableCell><TokenChip input={r.llm_input_tokens} output={r.llm_output_tokens} /></TableCell>
                  <TableCell>
                    <Tooltip title="Regenerate summary" arrow><span>
                      <Button size="small" variant="outlined" onClick={() => triggerOne(r.id)} disabled={triggerRowId === r.id || r.disabled}>
                        {triggerRowId === r.id ? <CircularProgress size={16} /> : 'Generate'}
                      </Button>
                    </span></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main
// ═══════════════════════════════════════════════════════════════════════════════
function AiFeedSummary() {
  const [tab, setTab] = useState(0);
  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header"><div className="row"><div className="col-sm-6"><h3>AI Feed Summaries</h3></div></div></div>
        </div>
        <div className="container-fluid"><div className="row"><div className="col-sm-12">
          <Card><CardContent>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                <Tab label="Feed Summary History" />
                <Tab label="Match Hashtag Topics" />
                <Tab label="Feed Topic Summaries" />
              </Tabs>
            </Box>
            {tab === 0 && <HistoryTab />}
            {tab === 1 && <MatchTopicsTab />}
            {tab === 2 && <FeedTopicsTab />}
          </CardContent></Card>
        </div></div></div>
      </div>
    </Component>
  );
}

export default AiFeedSummary;
